import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Autocomplete,
  TextField,
  InputAdornment,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { TableNodata } from "../../components/TableNodata";
import { TableLoader } from "../../components/TableLoader";
import CustomPagination from "../../components/CustomPagination";
import ArticleIcon from "@mui/icons-material/Article";
import api from "../../api/client";


const headers = [
  { label: "S.no", align: "center" },
  { label: "Activity name", align: "left" },
  { label: "Email", align: "left" },
  { label: "Date & Time", align: "left" },
  { label: "Description", align: "left" },
];

const AuditLogs = () => {
  const [selectedActivity, setSelectedActivity] = useState(null);
  const [loading, setLoading] = useState(false);
  const [showTable, setShowTable] = useState(false);
  const [listData, setListData] = useState([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [activityOptions, setActivityOptions] = useState([]);
  const rowsPerPage = 10;

  const getAuditLogs = async () => {
    setLoading(true);
    try {
      const body = {
        page: page,
        size: rowsPerPage,
      };
      if (selectedActivity) {
        body.activity_name = selectedActivity.activityName;
      }
      const res = await api.post("/GetAuditLogs", body);
      setListData(res?.data?.data || []);
      setCount(res?.data?.count || 0);
    } catch (error) {
      console.error("Failed to fetch audit logs:", error);
      setListData([]);
      setCount(0);
    } finally {
      setLoading(false);
    }
  };

  const getActivityOptions = async () => {
    try {
      const res = await api.post("/ListAuditLogActivities");
      setActivityOptions(res?.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch activity options:", error);
      setActivityOptions([]);
    }
  };

  useEffect(() => {
    getActivityOptions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    getAuditLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, selectedActivity]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setShowTable(true);
    }, 200);
    return () => clearTimeout(timer);
  }, []);

  return (
    <Box sx={{ minHeight: "100vh", p: 2 }}>
      {/* Header */}
      <Grid
        sx={{
          display: "flex",
          justifyContent: "space-between",
          mb: 2,
          pb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Box className="icon-box">
            <ArticleIcon className="iconHeading" />
          </Box>
          <Typography sx={{ fontSize: { xs: 20, sm: 28 }, fontWeight: 700, color: "#0a0a0a" }}>
            Audit Logs
          </Typography>
        </Box>

        <Box display="flex" flexDirection="column" gap={0.8} sx={{ width: 260 }}>
          <Autocomplete
            size="small"
            options={activityOptions}
            getOptionLabel={(option) => option?.activityName || ""}
            isOptionEqualToValue={(option, value) =>
              option.activityId === value?.activityId
            }
            value={selectedActivity}
            onChange={(event, newValue) => {
              setSelectedActivity(newValue);
              setPage(0);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select activity"
                fullWidth
                variant="outlined"
                size="small"
                InputProps={{
                  ...params.InputProps,
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon sx={{ color: "#a2a2a2" }} />
                    </InputAdornment>
                  ),
                }}
              />
            )}
          />
        </Box>
      </Grid>

      {/* Table */}
      <TableContainer
        component={Paper}
        sx={{
          opacity: showTable ? 1 : 0,
          transform: showTable ? "translateY(0)" : "translateY(40px)",
          transition: "opacity 0.5s ease, transform 0.5s ease",
        }}
      >
        <Table>
          <TableHead>
            {/* TABLE HEADER */}
            <TableRow>
              {headers.map((head, i) => (
                <TableCell key={i} align={head.align} className="tableHeader">
                  <b>{head.label}</b>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          {loading ? (
            <TableCell colSpan={headers.length} align="center">
              <TableLoader message="Fetching audit logs..." />
            </TableCell>
          ) : (
            <TableBody className="tablebody">
              {listData.map((item, index) => (
                <TableRow
                  key={index}
                  hover
                  sx={{
                    backgroundColor: index % 2 === 1 ? "#FAFAFA" : "inherit",
                    "&:hover": { backgroundColor: "#f1f1f1" },
                  }}
                >
                  <TableCell align="center">
                    {page * rowsPerPage + index + 1}
                  </TableCell>
                  <TableCell>
                    <Typography sx={{ fontWeight: 600, fontSize: "14px", color: "#1a1a1a" }}>
                      {item.activity_name || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell>{item.user_email || "-"}</TableCell>
                  <TableCell>{item.created_at || "-"}</TableCell>
                  <TableCell sx={{ maxWidth: "400px" }}>
                    <Typography sx={{ fontSize: "13px", color: "#64748b" }}>
                      {item.description || "-"}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}

              {((page === 0 && listData?.length > rowsPerPage - 1) ||
                page !== 0) && (
                <CustomPagination
                  page={page}
                  setPage={setPage}
                  count={count}
                  rowsPerPage={rowsPerPage}
                  colSpan={headers.length}
                />
              )}

              {listData.length === 0 && (
                <TableRow>
                  <TableCell colSpan={headers.length} align="center">
                    <TableNodata />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          )}
        </Table>
      </TableContainer>
    </Box>
  );
};

export default AuditLogs;
